import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { networkManager } from '../services/NetworkManager';

interface Organization {
  organizationName: string;
  hasCredentials: boolean;
  numberOfCreatedData: number;
  numberOfCreatedDefinitions: number;
  hasClankerURL: boolean;
}

interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  refreshOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const orgId = networkManager.getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID found');
      }

      const response = await networkManager.makeRequest<any>(`/${orgId}/organization`);
      setOrganization(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch organization');
    } finally {
      setLoading(false);
    }
  };

  const refreshOrganization = async () => {
    await fetchOrganization();
  };

  useEffect(() => {
    fetchOrganization();
  }, []);

  const value: OrganizationContextType = {
    organization,
    loading,
    error,
    refreshOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
